import React from "react";
import { useTranslation } from "react-i18next";
import {
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";

const presence = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

/** Resolve colleague contact fields with employment fallbacks. */
export const resolveColleagueContact = (colleague) => {
  if (!colleague) {
    return {
      mobileWork: null,
      mobile: null,
      phone: null,
      email: null,
      employeeNumber: null,
    };
  }
  const employment = colleague.employment || {};
  const mobileWork =
    presence(colleague.mobile_work) ||
    presence(employment.phone) ||
    presence(colleague.phone);
  const mobile =
    presence(colleague.mobile) ||
    presence(employment.mobile) ||
    presence(colleague.mobile_phone);
  return {
    mobileWork,
    mobile,
    // Compact list/tel: same precedence as mobileWork || mobile
    phone: mobileWork || mobile,
    email: presence(colleague.email) || presence(employment.email),
    employeeNumber:
      presence(colleague.employee_number) ||
      presence(employment.employee_number),
  };
};

export const ColleagueDialog = ({ isOpen, onClose, colleague }) => {
  const { t } = useTranslation();

  if (!isOpen || !colleague) return null;

  const { mobileWork, mobile, email, employeeNumber } =
    resolveColleagueContact(colleague);
  const hasContact = Boolean(mobileWork || mobile || email);

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleEmail = (emailAddress) => {
    window.location.href = `mailto:${emailAddress}`;
  };

  const getRelationshipDisplay = (relationshipType) => {
    const relationships = {
      SPOUSE: t("colleague_dialog.relationship.spouse"),
      PARTNER: t("colleague_dialog.relationship.partner"),
      PARENT: t("colleague_dialog.relationship.parent"),
      CHILD: t("colleague_dialog.relationship.child"),
      SIBLING: t("colleague_dialog.relationship.sibling"),
    };
    return relationships[relationshipType] || relationshipType;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 rounded-full p-3">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {colleague.name || colleague.displayTitle}
                </h3>
                <p className="text-sm text-gray-500">
                  {t("colleague_dialog.subtitle")}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">
                {t("colleague_dialog.contact_title")}
              </h4>

              {!hasContact && (
                <p className="text-sm text-gray-500">
                  {t("colleague_dialog.contact_missing")}
                </p>
              )}

              {mobileWork && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {t("colleague_dialog.mobile_work")}
                      </p>
                      <p className="text-sm text-gray-600 break-all">
                        {mobileWork}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-8">
                    <button
                      onClick={() => handleCall(mobileWork)}
                      className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
                    >
                      <PhoneIcon className="h-3 w-3" />
                      <span>{t("colleague_dialog.call")}</span>
                    </button>
                  </div>
                </div>
              )}

              {mobile && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {t("colleague_dialog.mobile")}
                      </p>
                      <p className="text-sm text-gray-600 break-all">{mobile}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-8">
                    <button
                      onClick={() => handleCall(mobile)}
                      className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
                    >
                      <PhoneIcon className="h-3 w-3" />
                      <span>{t("colleague_dialog.call")}</span>
                    </button>
                  </div>
                </div>
              )}

              {email && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {t("colleague_dialog.email")}
                      </p>
                      <p className="text-sm text-gray-600 break-all">{email}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-8">
                    <button
                      onClick={() => handleEmail(email)}
                      className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors duration-200"
                    >
                      <EnvelopeIcon className="h-3 w-3" />
                      <span>{t("colleague_dialog.send_email")}</span>
                    </button>
                  </div>
                </div>
              )}

              {colleague.department && (
                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t("colleague_dialog.department")}
                    </p>
                    <p className="text-sm text-gray-600">
                      {colleague.department.displayTitle}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {colleague.user_next_of_kins &&
              colleague.user_next_of_kins.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900">
                    {t("colleague_dialog.next_of_kin")}
                  </h4>
                  {colleague.user_next_of_kins.map((kin, index) => (
                    <div
                      key={kin.id || index}
                      className="bg-gray-50 rounded-lg p-3"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900">
                            {kin.name}
                          </p>
                          <span className="text-xs text-gray-500">
                            {getRelationshipDisplay(kin.relationship_type)}
                          </span>
                        </div>

                        {kin.phone_number && (
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <PhoneIcon className="h-3 w-3 text-gray-400" />
                              <p className="text-xs text-gray-600 break-all">
                                {kin.phone_number}
                              </p>
                            </div>
                            <div className="ml-5">
                              <button
                                onClick={() => handleCall(kin.phone_number)}
                                className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors duration-200"
                              >
                                <PhoneIcon className="h-2 w-2" />
                                <span>{t("colleague_dialog.call")}</span>
                              </button>
                            </div>
                          </div>
                        )}

                        {kin.email && (
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <EnvelopeIcon className="h-3 w-3 text-gray-400" />
                              <p className="text-xs text-gray-600 break-all">
                                {kin.email}
                              </p>
                            </div>
                            <div className="ml-5">
                              <button
                                onClick={() => handleEmail(kin.email)}
                                className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors duration-200"
                              >
                                <EnvelopeIcon className="h-2 w-2" />
                                <span>{t("colleague_dialog.send_email")}</span>
                              </button>
                            </div>
                          </div>
                        )}

                        {kin.address && (
                          <div className="flex items-start space-x-2">
                            <span className="text-xs text-gray-400" aria-hidden>
                              📍
                            </span>
                            <p className="text-xs text-gray-600 break-words">
                              {kin.address}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {employeeNumber && (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-gray-900">
                    {t("colleague_dialog.employee_number")}
                  </p>
                  <p className="text-sm text-gray-600 break-all">
                    {employeeNumber}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              {t("colleague_dialog.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
